
/**
 * Main recipe transformation logic
 */

import { decodeHtmlEntities } from './htmlDecoder';
import { determineRecipeType } from './recipeTypeDetector';
import { simplifyInstruction } from './instructionSimplifier';
import { generateContextAwareTip } from './tipGenerator';

/**
 * Transforms raw recipe data into a kid-friendly format
 */
export const transformRecipeData = (data: any) => {
  // Track used tips to avoid duplicates
  const usedTips = new Set<string>();
  
  // Decode and format temperature if it exists
  const temperature = data.temperature ? decodeHtmlEntities(data.temperature) : null;
  
  // Add preheat instruction if temperature exists
  const preheatingInstruction = temperature 
    ? [`First, ask a grown-up to preheat the oven to ${temperature}.`]
    : [];

  // Add timing information if available
  const timingInfo = data.cookTime 
    ? [`This recipe needs to cook for ${data.cookTime} minutes.`]
    : [];

  // Get recipe type for context-aware tips
  const recipeType = determineRecipeType(data.title, data.ingredients || []);
  
  // Clean ingredients for better processing
  const cleanIngredients = (data.ingredients || []).map((ingredient: string) => decodeHtmlEntities(ingredient));

  return {
    title: decodeHtmlEntities(data.title || "Recipe"),
    imageUrl: data.imageUrl,
    ingredients: cleanIngredients,
    steps: [
      ...preheatingInstruction,
      ...timingInfo,
      ...(data.instructions || [])
    ]
      .map((instruction: string) => {
        // Decode HTML entities before simplifying
        const decodedInstruction = decodeHtmlEntities(instruction);
        const simplifiedInstruction = simplifyInstruction(decodedInstruction);
        const tip = generateContextAwareTip(simplifiedInstruction, recipeType, usedTips, cleanIngredients);
        if (tip) {
          usedTips.add(tip);
          return {
            instruction: simplifiedInstruction,
            science: tip
          };
        }
        return {
          instruction: simplifiedInstruction
        };
      })
      .filter(step => step.instruction.length > 0),
    prepTime: data.prepTime,
    cookTime: data.cookTime,
    temperature: temperature
  };
};
